import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../shared/hooks/useAuthStore';
import { logout as logoutApi } from '../shared/api/auth';
import { identityApi } from '../shared/api/identity';
import { NotificationBell } from './NotificationBell';
import { LocalClock } from './LocalClock';

const iconProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const ChevronDownIcon = () => (
  <svg {...iconProps} width={14} height={14}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const LogoutIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
);

/* Map a path segment to a friendly label. */
const SEGMENT_LABELS: Record<string, string> = {
  crm: 'CRM',
  contacts: 'Contacts',
  companies: 'Companies',
  pipeline: 'Pipeline',
  boards: 'Boards',
  contracts: 'Contracts',
  reports: 'Reports',
  qualification: 'Qualification',
  settings: 'Settings',
  new: 'New',
  report: 'Report',
};

function humanize(seg: string): string {
  if (SEGMENT_LABELS[seg]) return SEGMENT_LABELS[seg];
  if (seg.length > 14) return seg.slice(0, 6) + '…' + seg.slice(-4);
  return seg.charAt(0).toUpperCase() + seg.slice(1);
}

/**
 * Resolve a cuid-looking path segment to a friendly name by inspecting the
 * React Query cache. The detail pages keep entities under known query keys
 * (`['contact', id]`, `['company', id]`, etc.) so we can read the name
 * without firing a new request. Falls back to the truncated cuid if the
 * cache hasn't hydrated for that ID yet.
 */
function resolveSegmentLabel(
  parts: string[],
  index: number,
  raw: string,
  cache: ReturnType<typeof useQueryClient>,
): string {
  // Only the LAST segment of a deep route is the ID. Pattern: parent/<id>.
  const parent = parts[index - 1];
  if (!parent || raw.length < 20) return humanize(raw);

  type WithName = { name?: string };
  type WithContact = { firstName?: string; lastName?: string };

  const tryName = (data: unknown): string | null => {
    if (!data || typeof data !== 'object') return null;
    const d = data as WithName & WithContact;
    if (d.firstName || d.lastName) {
      return [d.firstName, d.lastName].filter(Boolean).join(' ');
    }
    if (typeof d.name === 'string' && d.name) return d.name;
    return null;
  };

  // Try every plausible query key the detail pages use.
  const probeKeys: Array<[string, string]> = [
    ['contacts', 'contact'],
    ['companies', 'company'],
    ['contracts', 'contract'],
    ['boards', 'board'],
    ['pipeline', 'pipeline'],
    ['reports', 'report'],
    ['qualification', 'assessment'],
  ];
  for (const [route, queryKey] of probeKeys) {
    if (parent !== route) continue;
    // Try the per-entity cache.
    const cached = cache.getQueryData([queryKey, raw]);
    const name = tryName(cached);
    if (name) return name;
    // Try board-style queries that use a different key shape.
    const altCached = cache.getQueryData([queryKey, raw, 'detail']);
    const altName = tryName(altCached);
    if (altName) return altName;
  }
  return humanize(raw);
}

function buildCrumbs(
  pathname: string,
  cache: ReturnType<typeof useQueryClient>,
): { label: string; to: string }[] {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return [];
  const crumbs: { label: string; to: string }[] = [];
  let acc = '';
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]!;
    acc += '/' + p;
    crumbs.push({ label: resolveSegmentLabel(parts, i, p, cache), to: acc });
  }
  return crumbs;
}

function initials(name?: string, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '');
  }
  if (email) return email[0]?.toUpperCase() ?? '?';
  return '?';
}

export function Topbar({ onMenuClick }: { onMenuClick: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  // Pull avatar/timezone preference. The Settings page invalidates this key
  // when the user updates their profile, so the avatar updates live.
  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: () => identityApi.me(),
    staleTime: 60_000,
  });
  const avatarUrl = meQ.data?.avatarUrl ?? null;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Force the Topbar to re-render whenever the React Query cache mutates so
  // breadcrumb labels resolve once the detail page fills in `['contact', id]`
  // / `['company', id]` / etc. Without this the breadcrumb shows the truncated
  // cuid on first render and never updates because Topbar has no observers
  // on those queries.
  const [, bumpCacheTick] = useState(0);
  useEffect(() => {
    const unsub = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'added' || event.type === 'updated') {
        bumpCacheTick((n) => n + 1);
      }
    });
    return unsub;
  }, [queryClient]);

  // Close on outside click / escape. The menu panel is portalled to body
  // so it sits above modals/drawers regardless of stacking context - same
  // pattern as NotificationBell. We treat clicks inside the trigger
  // (menuRef) OR inside any [data-profile-popover] as "inside".
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const insideTrigger =
        menuRef.current && menuRef.current.contains(target as Node);
      const insidePopover = target?.closest?.('[data-profile-popover]');
      if (!insideTrigger && !insidePopover) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const crumbs = buildCrumbs(location.pathname, queryClient);
  const last = crumbs[crumbs.length - 1];

  function handleLogout() {
    setMenuOpen(false);
    try {
      // Preferred: preload-exposed API if present
      const w = window as unknown as { tcCrm?: { auth?: { logout?: () => unknown } } };
      const fn = w.tcCrm?.auth?.logout;
      if (typeof fn === 'function') {
        Promise.resolve(fn()).catch(() => undefined);
      }
    } catch {
      /* ignore */
    }
    try {
      logoutApi();
    } catch {
      /* ignore */
    }
    try {
      localStorage.clear();
    } catch {
      /* ignore */
    }
    setUser(null);
    navigate('/login', { replace: true });
  }

  return (
    <header
      className="flex h-14 shrink-0 items-center justify-between gap-4
                 surface-glass px-4 border-b border-slate-200/60 dark:border-white/10"
    >
      {/* Mobile nav toggle - opens the off-canvas sidebar drawer. Hidden on md+. */}
      <button
        type="button"
        onClick={onMenuClick}
        className="btn-icon -ml-1 shrink-0 md:hidden"
        aria-label="Open navigation menu"
      >
        <svg {...iconProps}>
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
      </button>

      {/* Breadcrumb / page title */}
      <div className="min-w-0 flex-1">
        {crumbs.length > 0 ? (
          <nav aria-label="Breadcrumb" className="min-w-0">
            <ol className="flex min-w-0 items-center gap-1.5 text-sm">
              {crumbs.slice(0, -1).map((c, i) => (
                <li key={i} className="flex min-w-0 items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => navigate(c.to)}
                    className="truncate rounded px-1 text-slate-500 transition-colors
                               hover:text-slate-900 focus:outline-none
                               focus-visible:ring-2 focus-visible:ring-brand-500
                               dark:text-slate-400 dark:hover:text-slate-100"
                  >
                    {c.label}
                  </button>
                  <span className="text-slate-300 dark:text-slate-600">/</span>
                </li>
              ))}
              {last && (
                <li className="min-w-0">
                  <span className="truncate font-semibold text-slate-900 dark:text-slate-50">
                    {last.label}
                  </span>
                </li>
              )}
            </ol>
          </nav>
        ) : (
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-50">
            Tricorian CRM
          </span>
        )}
      </div>

      {/* Local time + date — user's locale, updates every 30s. Hidden on the
          smallest screens to keep the mobile topbar uncluttered. */}
      <div className="hidden sm:block">
        <LocalClock />
      </div>

      {/* Notifications */}
      <NotificationBell />

      {/* User menu */}
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className="flex items-center gap-2 rounded-lg p-1 pr-2
                     transition-colors hover:bg-slate-100
                     dark:hover:bg-white/5
                     focus:outline-none focus-visible:ring-2
                     focus-visible:ring-accent-400/60 focus-visible:ring-offset-2
                     focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-950"
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="h-7 w-7 rounded-full object-cover ring-1 ring-slate-200 dark:ring-white/10"
            />
          ) : (
            <span
              className="relative flex h-7 w-7 items-center justify-center rounded-full
                         bg-gradient-brand text-xs font-semibold text-white shadow-glow-brand"
              aria-hidden="true"
            >
              <span className="relative z-10">{initials(user?.displayName, user?.email)}</span>
            </span>
          )}
          <span className="hidden text-left leading-tight md:block">
            <span className="block max-w-[160px] truncate text-sm font-medium text-slate-900 dark:text-slate-100">
              {user?.displayName ?? user?.email ?? 'Account'}
            </span>
          </span>
          <ChevronDownIcon />
        </button>

        {menuOpen && createPortal(
          <div
            role="menu"
            data-profile-popover
            // Portalled into document.body so it escapes the topbar's
            // backdrop-filter stacking context and sits above modals/drawers.
            className="fixed right-4 top-14 z-[1000] w-64
                       overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800
                       bg-white dark:bg-slate-900 shadow-soft-lg
                       animate-slide-up"
          >
            <div className="border-b border-slate-100 p-3 dark:border-slate-800">
              <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                {user?.displayName ?? 'Signed in'}
              </div>
              <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                {user?.email ?? '-'}
              </div>
              {user?.role && (
                <div className="mt-2">
                  <span className="badge-neutral">{user.role}</span>
                </div>
              )}
            </div>
            <div className="p-1">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/settings');
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2
                           text-left text-sm text-slate-700
                           transition-colors hover:bg-slate-100
                           dark:text-slate-200 dark:hover:bg-slate-800
                           focus:outline-none focus-visible:bg-slate-100
                           dark:focus-visible:bg-slate-800"
              >
                <span>Settings & preferences</span>
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2
                           text-left text-sm text-slate-700
                           transition-colors hover:bg-slate-100
                           dark:text-slate-200 dark:hover:bg-slate-800
                           focus:outline-none focus-visible:bg-slate-100
                           dark:focus-visible:bg-slate-800"
              >
                <LogoutIcon />
                <span>Sign out</span>
              </button>
            </div>
          </div>,
          document.body,
        )}
      </div>
    </header>
  );
}
